import {
    Component,
    OnInit,
    Input,
    ContentChildren,
    QueryList,
    AfterContentInit,
    ViewChild,
    ElementRef,
    OnDestroy,
    Output,
    EventEmitter
} from '@angular/core';
import { TabComponent } from './../tab/tab.component';

@Component({
    selector: 'ngxui-tabs',
    templateUrl: './tabs.component.html',
    styleUrls: ['./tabs.component.scss']
})
export class TabsComponent implements OnInit, AfterContentInit, OnDestroy {
    @Input() ReadOnly: boolean;
    @Input() Orientation: string;
    @Input() Theme: string;
    @Input() TabText: string;
    @Input() HideAddTab: boolean = false;

    @Output() TabAddEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() TabCloseEvent: EventEmitter<any> = new EventEmitter<any>();

    @ContentChildren(TabComponent) tabs: QueryList<TabComponent>;
    @ViewChild('tabContent') tabContent: ElementRef;

    TAB_CONTENT_OVERLAP_DISTANCE = 1;
    TAB_CONTENT_MARGIN = 9;
    TAB_CONTENT_MIN_WIDTH = 24;
    TAB_CONTENT_MAX_WIDTH = 240;

    TAB_SIZE_SMALL = 84;
    TAB_SIZE_SMALLER = 60;
    TAB_SIZE_MINI = 48;

    get tabContentWidths() {
        if (this.tabContent) {
            const numberOfTabs = this.tabs.length;
            const tabsContentWidth = this.tabContent.nativeElement.clientWidth;
            const tabsCumulativeOverlappedWidth = (numberOfTabs - 1) * this.TAB_CONTENT_OVERLAP_DISTANCE;
            const targetWidth = (tabsContentWidth - (2 * this.TAB_CONTENT_MARGIN) + tabsCumulativeOverlappedWidth) / numberOfTabs;
            const clampedTargetWidth = Math.max(this.TAB_CONTENT_MIN_WIDTH, Math.min(this.TAB_CONTENT_MAX_WIDTH, targetWidth));
            const flooredClampedTargetWidth = Math.floor(clampedTargetWidth);
            const totalTabsWidthUsingTarget = (flooredClampedTargetWidth * numberOfTabs) + (2 * this.TAB_CONTENT_MARGIN) - tabsCumulativeOverlappedWidth;
            const totalExtraWidthDueToFlooring = tabsContentWidth - totalTabsWidthUsingTarget;

            // TODO - Support tabs with different widths / e.g. "pinned" tabs
            const widths = [];
            let extraWidthRemaining = totalExtraWidthDueToFlooring;

            this.tabs.toArray().forEach((tabEl, i) => {
                const extraWidth = flooredClampedTargetWidth < this.TAB_CONTENT_MAX_WIDTH && extraWidthRemaining > 0 ? 1 : 0;
                widths.push(flooredClampedTargetWidth + extraWidth);
                if (extraWidthRemaining > 0) {
                    extraWidthRemaining -= 1;
                }
            });
            return widths;
        } else {
            return [];
        }
    }

    get tabContentPositions() {
        const positions = [];
        const tabContentWidths = this.tabContentWidths;

        let position = this.TAB_CONTENT_MARGIN;
        tabContentWidths.forEach((width, i) => {
          const offset = i * this.TAB_CONTENT_OVERLAP_DISTANCE;
          positions.push(position - offset);
          position += width;
        });

        return positions;
    }

    get tabPositions() {
        const positions = [];
        this.tabContentPositions.forEach((contentPosition, i) => {
            this.tabs.toArray()[i].tabPosition = (contentPosition - this.TAB_CONTENT_MARGIN);
            positions.push(contentPosition - this.TAB_CONTENT_MARGIN);
        });

        return positions;
    }

    constructor() {}

    ngOnDestroy() {}

    ngOnInit() {}

    ngAfterContentInit() {
        // get all active tabs
        const activeTabs = this.tabs.filter(tab => tab.Active);

        // if there is no active tab set, activate the first
        if (activeTabs.length === 0) {
            this.selectTab(this.tabs.first);
        }
        this.layoutTabs();
    }

    selectTab(tab: TabComponent) {
        if (tab) {
            // deactivate all tabs
            this.tabs.toArray().forEach(tab => (tab.Active = false));
            // activate the tab the user has clicked on.
            tab.Active = true;
            this.layoutTabs();
        }
    }

    getTabPosition(tab: TabComponent){
        return `translate3d(${tab.tabPosition}px, 0, 0)`;
    }

    layoutTabs() {
        const tabContentWidths = this.tabContentWidths;

        this.tabs.toArray().forEach((tabEl, i) => {
            const contentWidth = tabContentWidths[i];
            const width = contentWidth + (2 * this.TAB_CONTENT_MARGIN);
            tabEl.contentWidth = contentWidth;
            tabEl.tabWidth = width;
        });
        this.tabPositions.forEach((position, i) => {

        });
    }

    isSmall(tab: TabComponent) {
        try {
            if (tab.contentWidth < this.TAB_SIZE_SMALL) {
                return '';
            } else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }

    isSmaller(tab: TabComponent) {
        try {
            if (tab.contentWidth < this.TAB_SIZE_SMALLER) {
                return '';
            } else {
                return null;
            }
        } catch (ex) {
            return null;
        }

    }

    isMini(tab: TabComponent) {
        try {
            if (tab.contentWidth < this.TAB_SIZE_MINI) {
                return '';
            } else {
                return null;
            }
        } catch (ex) {
            return null;
        }

    }

    isActive(tab: TabComponent){
        try {
            if (tab.Active) {
                return '';
            } else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }

    onAddTab() {
        this.TabAddEvent.emit();
    }

    addTab(tab: TabComponent): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                const  time = new Date();
                const allTabs = this.tabs.toArray();
                tab.TabId = time.getTime();
                this.tabs.reset([...allTabs, tab]);
                this.layoutTabs();
                this.selectTab(tab);
                resolve(this.tabs);
            } catch (ex) {
                reject(ex);
            }
        });
    }

    closeTab(tab: TabComponent) {
        tab.Deleted = true;
        const index = this.tabs.toArray().findIndex((e) => e.TabId === tab.TabId) + 1;
        this.tabs.reset(this.tabs.filter(e => e.TabId !== tab.TabId));
        if (this.tabs.length > 0) {
            if (index > this.tabs.length) {
                this.selectTab(this.tabs.last);
            } else  if (index === 1) {
                this.selectTab(this.tabs.first);
            } else if (index > 1) {
                this.selectTab(this.tabs.toArray()[index - 1]);
            }
        }
        this.layoutTabs();
        this.TabCloseEvent.emit(tab);
    }
}

